import { useMemo, useState } from "react";
import { Box, Paper, useTheme } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {useAppNavigate} from "../../../_hooks/use-app-navigate";
import {useApi} from "../../../_hooks/use-api";
import {RootState} from "../../../store/store";
import {_adjustInvestment} from "../../../utils/_adjustInvestment";
import {_roundDec} from "../../../utils/_roundToDecimals";
import {precision} from "../../../_utils/precision";
import {calculateStepsCount} from "../../../utils/utils";
import {showNotification} from "../../../store/slices/notificationSlice";
import {APP_ID} from "../../../_app/consts";
import {setCustomGridMode} from "../../../store/slices/customBotSlice";
import {HubError} from "../../../class/Error";
import {ButtonStyled} from "../../../_components/ButtonStyled";
import Loading from "../../../_components/Loading";
import { Account, Bot, BotCreate } from "../../../_app/types";

interface Props {
  bot: Bot;
  activeAccount: Account;
  stepInBase: number;
  stepInQuote: number;
  stepsCount: number;
}

const FuturesBotLaunch = ({ bot, stepInBase, stepInQuote, stepsCount, activeAccount }: Props) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const appNavigate = useAppNavigate();
  const api = useApi();
  const [isDisabled, setIsDisabled] = useState(false);
  const activeAccountID = useSelector((state: RootState) => state.user.activeAccount.id);
  const CustomGridMode = useSelector((state: RootState) => state.customGridMode);

  const calculateOrderAmount = (
    bot: Bot,
    stepInBase: number,
    stepInQuote: number,
    stepsCount: number
  ) => {
    if (stepsCount === Infinity) {
      const adjustedInvestment = _adjustInvestment(
        bot.investment,
        bot.currencyType === "BASE" ? stepInBase : stepInQuote
      );
      const steps =
        bot.currencyType === "BASE"
          ? Number((adjustedInvestment / stepInBase).toFixed(0))
          : Number((adjustedInvestment / stepInQuote).toFixed(0));

      return _roundDec(steps, precision(bot.ticker.orderStep)) * bot.ticker.orderStep;
    }
    return _roundDec(bot.ticker.orderStep * stepsCount, precision(bot.ticker.orderStep));
  };

  const orderSize = useMemo(() => {
    let steps =
      bot.currencyType === "BASE"
        ? _roundDec(bot.investment / stepInBase, 0)
        : Number((bot.investment / stepInQuote).toFixed(0));
    return _roundDec(bot.ticker.orderStep * steps, precision(bot.ticker.orderStep));
  }, [bot.investment, stepInBase, stepInQuote]);

  const createBot = async () => {
    setIsDisabled(true);
    const ORDER_AMOUNT = calculateOrderAmount(bot, stepInBase, stepInQuote, stepsCount);
    const STEPS_COUNT = calculateStepsCount(bot, stepInBase, stepInQuote, stepsCount);

    const initialAmount = bot.currencyType === "BASE" ? bot.investment : STEPS_COUNT * stepInBase;
    let initialAmountQuote = 0;
    if (bot.currencyType === "QUOTE") {
      initialAmountQuote = _adjustInvestment(bot.investment, stepInQuote);
    } else {
      initialAmountQuote = STEPS_COUNT * stepInQuote;
    }

    let createNewBot: BotCreate | undefined = {
      base: bot.base,
      quote: bot.quote,
      minPrice: _roundDec(bot.minPrice, precision(bot.ticker.priceStep)),
      maxPrice: _roundDec(bot.maxPrice, precision(bot.ticker.priceStep)),
      botType: "FUTURES_GRID",
      startPrice: _roundDec(bot.startPrice, precision(bot.ticker.priceStep)),
      initialGrids: bot.gridsAmount,
      initialAmount: initialAmount, // тут всегда в base отправляем
      currencyType: bot.currencyType,
      gridDirection: bot.gridDirection.toUpperCase(),
      leverage: bot.leverage,
      gridType: bot.gridType,
      priceStep: bot.priceStep,
      priceStepPercentage: bot.priceStepPercentage,
      accountId: activeAccount.id,
      initialAmountQuote: _roundDec(initialAmountQuote, precision(bot.ticker.orderStep)),
      isAutoManaged: false,
      orderAmount: orderSize,
      closingAtInitialPrice: false,
    };

    if (bot.customGridSettings.length > 0 && CustomGridMode) {
      createNewBot.customGridSettings = bot.customGridSettings;
    }

    try {
      await api.launchCreateBot(createNewBot);
      dispatch(
        showNotification({
          message: `Your new trading bot ${bot.base}/${bot.quote} is being created. It will take a few seconds.`,
          severity: "success",
        })
      );
      appNavigate.botList("active");
      localStorage.setItem(`leverage${APP_ID}_${activeAccountID}`, String(bot.leverage));
      dispatch(setCustomGridMode(false));
      createNewBot = undefined;
    } catch (error: any) {
      const message =
        error instanceof HubError ? `${error.message} ${error.detailedMessage}` : error.message;

      dispatch(showNotification({ message, severity: "error" }));
    } finally {
      setIsDisabled(false);
    }
  };

  return (
    <Paper
      sx={{ marginTop: "12px" }}
      className="base-adron-paper paper-light"
      style={{
        backgroundColor: theme.palette.background.paper,
        boxShadow: "0px -4px 6px -1px rgba(0, 0, 0, 0.10",
      }}
    >
      <ButtonStyled
        onClick={createBot}
        disabled={isDisabled}
        style={{ padding: "8px 16px", color: "#fff", height: "40px" }}
        fullWidth={true}
        color={"primary"}
        variant="contained"
      >
        {isDisabled ? (
          <Loading color={"inherit"} size={20} />
        ) : (
          <Box style={{height: "20px"}}>
						Launch bot
					</Box>
        )}
      </ButtonStyled>
    </Paper>
  );
};

export default FuturesBotLaunch;
